import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { listBillingRecords } from '../api/endpoints/billing';
import { createExpense, listExpenses, patchExpense } from '../api/endpoints/expenses';
import { getPnl } from '../api/endpoints/pnl';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import type { ExpenseCategory, ExpenseRecord } from '../api/schemas';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { FinanzasTabs } from '../components/FinanzasTabs';
import { PageHeader } from '../components/PageHeader';
import { PnlChart } from '../components/PnlChart';
import { Money } from '../components/Figures';
import { CATEGORY_LABEL, defaultPnlRange, toDateInputValue } from '../lib/expenses';
import { formatDateOnly, formatDateTime, formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';

const EXPENSES_QUERY_KEY = ['business-expenses'];
const BILLING_QUERY_KEY = ['business-billing-records'];
const PNL_QUERY_KEY = ['business-pnl'];

const CATEGORY_OPTIONS: ExpenseCategory[] = [
  'alquiler',
  'planilla',
  'insumos',
  'servicios',
  'afiliacion_dale',
  'otro',
];
const DEFAULT_CATEGORY: ExpenseCategory = 'otro';

/** Ingresos, Gastos y lo cobrado por Dale, en una sola pantalla (docs/CONCEPTS.md: Finanzas). */
export function FinanzasPage() {
  const queryClient = useQueryClient();
  const defaultRange = useMemo(() => defaultPnlRange(new Date()), []);
  const [fromInput, setFromInput] = useState(defaultRange.from);
  const [toInput, setToInput] = useState(defaultRange.to);
  const [range, setRange] = useState(defaultRange);

  const pnlQuery = useQuery({
    queryKey: [...PNL_QUERY_KEY, range.from, range.to],
    queryFn: () => getPnl(range.from, range.to),
  });

  function handleRangeSubmit(event: FormEvent) {
    event.preventDefault();
    setRange({ from: fromInput, to: toInput });
  }

  function invalidateExpensesAndPnl() {
    void queryClient.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PNL_QUERY_KEY });
  }

  const expensesQuery = useQuery({ queryKey: EXPENSES_QUERY_KEY, queryFn: listExpenses });
  const expenses = expensesQuery.data?.data ?? [];

  const [concept, setConcept] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>();
  const [category, setCategory] = useState<ExpenseCategory>(DEFAULT_CATEGORY);
  const [incurredOn, setIncurredOn] = useState(() => toDateInputValue(new Date()));
  const [isRecurringMonthly, setIsRecurringMonthly] = useState(false);
  const [recurringUntil, setRecurringUntil] = useState('');
  const createMutation = useMutationAttempt({
    mutationFn: createExpense,
    onSuccess: () => {
      setConcept('');
      setAmountInput('');
      setAmountError(undefined);
      setCategory(DEFAULT_CATEGORY);
      setIncurredOn(toDateInputValue(new Date()));
      setIsRecurringMonthly(false);
      setRecurringUntil('');
      invalidateExpensesAndPnl();
    },
  });
  const saving = createMutation.isPending || createMutation.uncertain;

  function handleCreateSubmit(event: FormEvent) {
    event.preventDefault();
    if (createMutation.uncertain) { createMutation.retry(); return; }
    const price = parsePriceInput(amountInput);
    if (!price.ok) {
      setAmountError(price.message);
      return;
    }
    setAmountError(undefined);
    createMutation.mutate({ concept, amountCents: price.cents, category, incurredOn, isRecurringMonthly,
      ...(isRecurringMonthly && recurringUntil.trim() !== '' ? { recurringUntil } : {}) });
  }

  const billingQuery = useInfiniteQuery({
    queryKey: BILLING_QUERY_KEY,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => listBillingRecords(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
  const billingRecords = billingQuery.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <main>
      <PageHeader title="Finanzas" description="Ingresos, gastos y lo cobrado por Dale." />
      <FinanzasTabs />

      <section className="section" aria-label="Resumen">
        <h2 className="section-label">Resumen</h2>
        <form onSubmit={handleRangeSubmit} aria-label="Rango de fechas" className="filter-row">
          <div className="field">
            <label className="field-label" htmlFor="finanzas-desde">
              Desde
            </label>
            <input
              id="finanzas-desde"
              type="date"
              value={fromInput}
              onChange={(event) => setFromInput(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="finanzas-hasta">
              Hasta
            </label>
            <input
              id="finanzas-hasta"
              type="date"
              value={toInput}
              onChange={(event) => setToInput(event.target.value)}
            />
          </div>
          <button type="submit" className="btn small secondary">
            Aplicar
          </button>
        </form>

        {pnlQuery.isLoading ? <LoadingState label="Cargando resumen…" /> : null}
        {pnlQuery.isError ? <ErrorBanner message={errorMessage(pnlQuery.error)} /> : null}
        {pnlQuery.data ? (
          <>
            <div className="pnl-figures">
              <div className="pnl-figure">
                <p className="label">Ingresos</p>
                <p className="value">
                  <Money cents={pnlQuery.data.revenueCents} />
                </p>
              </div>
              <div className="pnl-figure">
                <p className="label">Gastos</p>
                <p className="value">
                  <Money cents={pnlQuery.data.expenseCents} />
                </p>
              </div>
              <div className="pnl-figure net">
                <p className="label">Neto</p>
                <p className="value" data-negative={pnlQuery.data.netCents < 0 || undefined}>
                  <Money cents={pnlQuery.data.netCents} />
                </p>
              </div>
            </div>
            <PnlChart months={pnlQuery.data.months} />
          </>
        ) : null}
      </section>

      <section className="section" aria-label="Gastos">
        <h2 className="section-label">Gastos</h2>
        {expensesQuery.isLoading ? <LoadingState label="Cargando gastos…" /> : null}
        {expensesQuery.isError ? <ErrorBanner message={errorMessage(expensesQuery.error)} /> : null}
        {expensesQuery.data ? (
          expenses.length === 0 ? (
            <EmptyState
              title="Todavía no tienes gastos."
              description="Registra lo que sale: alquiler, planilla, insumos y más."
            />
          ) : (
            <ul className="rows">
              {expenses.map((expense) => (
                <li key={expense.id}>
                  <ExpenseRow expense={expense} onChanged={invalidateExpensesAndPnl} />
                </li>
              ))}
            </ul>
          )
        ) : null}

        <div className="section" aria-label="Agregar gasto">
          <h2 className="section-label">Agregar gasto</h2>
          <form className="card form-grid cols-2" onSubmit={handleCreateSubmit}>
            <FormField label="Concepto" htmlFor="expense-concept" className="span-2">
              <input
                id="expense-concept"
                disabled={saving}
                value={concept}
                onChange={(event) => setConcept(event.target.value)}
                required
              />
            </FormField>
            <FormField
              label="Monto"
              htmlFor="expense-amount"
              error={amountError}
              hint="En dólares, por ejemplo 45.00"
            >
              <input
                id="expense-amount"
                disabled={saving}
                inputMode="decimal"
                placeholder="45.00"
                value={amountInput}
                onChange={(event) => setAmountInput(event.target.value)}
                required
              />
            </FormField>
            <FormField label="Categoría" htmlFor="expense-category">
              <select
                id="expense-category"
                disabled={saving}
                value={category}
                onChange={(event) => setCategory(event.target.value as ExpenseCategory)}
              >
                {CATEGORY_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {CATEGORY_LABEL[value]}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Fecha" htmlFor="expense-incurred-on">
              <input
                id="expense-incurred-on"
                disabled={saving}
                type="date"
                value={incurredOn}
                onChange={(event) => setIncurredOn(event.target.value)}
                required
              />
            </FormField>
            <div className="field">
              <label className="field-label" htmlFor="expense-recurring">
                <input
                  id="expense-recurring"
                  disabled={saving}
                  type="checkbox"
                  checked={isRecurringMonthly}
                  onChange={(event) => setIsRecurringMonthly(event.target.checked)}
                />{' '}
                Se repite cada mes
              </label>
            </div>
            {isRecurringMonthly ? (
              <FormField label="Hasta" htmlFor="expense-recurring-until" hint="Opcional">
                <input
                  id="expense-recurring-until"
                  disabled={saving}
                  type="date"
                  value={recurringUntil}
                  onChange={(event) => setRecurringUntil(event.target.value)}
                />
              </FormField>
            ) : null}
            {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
            <button type="submit" className="btn" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Agregando…' : 'Agregar gasto'}
            </button>
          </form>
        </div>
      </section>

      <section className="section" aria-label="Cobros de Dale">
        <h2 className="section-label">Cobros de Dale</h2>
        {billingQuery.isLoading ? <LoadingState label="Cargando cobros…" /> : null}
        {billingQuery.isError ? <ErrorBanner message={errorMessage(billingQuery.error)} /> : null}
        {!billingQuery.isLoading && !billingQuery.isError && billingRecords.length === 0 ? (
          <EmptyState
            title="Todavía no hay cobros."
            description="Cuando una visita completada se cobre, el recibo queda aquí."
          />
        ) : null}
        {billingRecords.length > 0 ? (
          <div className="table-scroll">
            <table className="stack-table">
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Monto</th>
                  <th scope="col">Proveedor</th>
                  <th scope="col">Recibo</th>
                </tr>
              </thead>
              <tbody>
                {billingRecords.map((record) => (
                  <tr key={record.id}>
                    <td data-cell="lead">{formatDateTime(record.paidAt)}</td>
                    <td data-cell="end" className="amount">{formatMoneyCents(record.amountCents)}</td>
                    <td data-cell="lead">{record.provider}</td>
                    <td data-cell="end" className="muted-cell">{record.receiptId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {billingQuery.hasNextPage ? (
          <button
            type="button"
            className="btn small secondary"
            onClick={() => void billingQuery.fetchNextPage()}
            disabled={billingQuery.isFetchingNextPage}
          >
            {billingQuery.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
          </button>
        ) : null}
      </section>
    </main>
  );
}

type ExpenseRowProps = { expense: ExpenseRecord; onChanged: () => void };

function ExpenseRow({ expense, onChanged }: ExpenseRowProps) {
  const deactivateKey = useRef(createIdempotencyKeyHolder()).current;
  const endRecurrenceKey = useRef(createIdempotencyKeyHolder()).current;

  const deactivateMutation = useMutation({
    mutationFn: () => patchExpense(expense.id, { isActive: false }, deactivateKey.ensure()),
    onSuccess: onChanged,
    onSettled: () => deactivateKey.reset(),
  });

  const endRecurrenceMutation = useMutation({
    mutationFn: () =>
      patchExpense(expense.id, { recurringUntil: toDateInputValue(new Date()) }, endRecurrenceKey.ensure()),
    onSuccess: onChanged,
    onSettled: () => endRecurrenceKey.reset(),
  });

  const canEndRecurrence = expense.isActive && expense.isRecurringMonthly && !expense.recurringUntil;

  return (
    <div className="row">
      <div className="grow">
        <p className="row-title">{expense.concept}</p>
        <p className="row-sub">
          {CATEGORY_LABEL[expense.category]} · {formatDateOnly(expense.incurredOn)}
          {expense.isRecurringMonthly ? ' · mensual' : ''}
        </p>
        {deactivateMutation.isError ? <ErrorBanner message={errorMessage(deactivateMutation.error)} /> : null}
        {endRecurrenceMutation.isError ? (
          <ErrorBanner message={errorMessage(endRecurrenceMutation.error)} />
        ) : null}
      </div>
      <span className="amount">{formatMoneyCents(expense.amountCents)}</span>
      <span className="action-pair">
        {canEndRecurrence ? (
          <ConfirmAction
            label="Terminar recurrencia"
            variant="secondary"
            pending={endRecurrenceMutation.isPending}
            onConfirm={() => endRecurrenceMutation.mutate()}
          />
        ) : null}
        {expense.isActive ? (
          <ConfirmAction
            label="Eliminar"
            variant="danger"
            pending={deactivateMutation.isPending}
            onConfirm={() => deactivateMutation.mutate()}
          />
        ) : null}
      </span>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
